import {
  Alert,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import {
  IconAlertCircle,
  IconPlus,
  IconRefresh,
  IconSearch,
} from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";
import { ReflexAdminRow } from "../../../components/admin/reflexes/ReflexAdminRow";
import { ReflexFormModal } from "../../../components/admin/reflexes/ReflexFormModal";
import { ReflexStatusConfirmModal } from "../../../components/admin/reflexes/ReflexStatusConfirmModal";
import {
  useCreateReflex,
  useCreateReflexVersion,
  useReflexAdminOptions,
  useReflexes,
  useUpdateReflexStatus,
} from "../../../hooks/admin/useReflexes";
import type {
  ReflexDefinition,
  ReflexListParams,
  ReflexSortField,
  ReflexSortOrder,
  SaveReflexInput,
} from "../../../services/admin/reflexes.service";

export default function ReflexesAdminPage() {
  const [editorOpened, editor] = useDisclosure(false);
  const [editing, setEditing] = useState<ReflexDefinition>();
  const [toggling, setToggling] = useState<ReflexDefinition>();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [status, setStatus] = useState<"active" | "inactive" | "">("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState<ReflexSortField>("name");
  const [sortOrder, setSortOrder] = useState<ReflexSortOrder>("asc");
  const params = useMemo<ReflexListParams>(
    () => ({
      page,
      limit: pageSize,
      search: debouncedSearch.trim() || undefined,
      status: status || undefined,
      sortBy,
      sortOrder,
    }),
    [debouncedSearch, page, pageSize, sortBy, sortOrder, status]
  );
  const query = useReflexes(params);
  const optionsQuery = useReflexAdminOptions();
  const create = useCreateReflex();
  const version = useCreateReflexVersion();
  const updateStatus = useUpdateReflexStatus();
  const reflexes = useMemo(() => query.data?.data ?? [], [query.data]);
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, query.data?.totalPages ?? 1);
  const panelOptions = useMemo(
    () =>
      (optionsQuery.data?.panels ?? []).map((item) => ({
        value: item.id,
        label: item.name,
      })),
    [optionsQuery.data]
  );
  const icdOptions = useMemo(
    () =>
      (optionsQuery.data?.icdCodes ?? []).map((item) => ({
        value: item.id,
        label: `${item.code} — ${item.description}`,
      })),
    [optionsQuery.data]
  );
  const isSaving = create.isPending || version.isPending;
  const visibleRange =
    total === 0
      ? "Showing 0 of 0"
      : `Showing ${(page - 1) * pageSize + 1}–${Math.min(
          page * pageSize,
          total
        )} of ${total}`;

  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.currentTarget.value);
      setPage(1);
    },
    []
  );
  const handleStatus = useCallback((value: string | null) => {
    setStatus((value ?? "") as typeof status);
    setPage(1);
  }, []);
  const handleSort = useCallback((value: string | null) => {
    const [field, order] = (value ?? "name:asc").split(":");
    setSortBy(field as ReflexSortField);
    setSortOrder(order as ReflexSortOrder);
    setPage(1);
  }, []);
  const handlePageSize = useCallback((value: string | null) => {
    setPageSize(Number(value ?? 20));
    setPage(1);
  }, []);
  const handleAdd = useCallback(() => {
    setEditing(undefined);
    editor.open();
  }, [editor]);
  const handleEdit = useCallback(
    (reflex: ReflexDefinition) => {
      setEditing(reflex);
      editor.open();
    },
    [editor]
  );
  const handleClose = useCallback(() => editor.close(), [editor]);
  const handleSave = useCallback(
    async (input: SaveReflexInput) => {
      if (editing) await version.mutateAsync({ id: editing.id, input });
      else await create.mutateAsync(input);
      editor.close();
    },
    [create, editing, editor, version]
  );
  const handleToggle = useCallback(
    (reflex: ReflexDefinition) => setToggling(reflex),
    []
  );
  const handleToggleClose = useCallback(() => setToggling(undefined), []);
  const handleToggleConfirm = useCallback(async () => {
    if (!toggling) return;
    try {
      await updateStatus.mutateAsync({
        id: toggling.id,
        isActive: !toggling.isActive,
      });
      setToggling(undefined);
    } catch {
      /* Hook displays the actionable error. */
    }
  }, [toggling, updateStatus]);
  const handleRefresh = useCallback(() => {
    query.refetch();
    optionsQuery.refetch();
  }, [optionsQuery, query]);

  return (
    <Container fluid py="xl">
      <Stack gap="lg" maw={1600} mx="auto">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2}>Reflexes</Title>
            <Text size="sm" c="dimmed" mt="xs">
              Configure starting panels and ICD-10 driven follow-on panel rules.
            </Text>
          </div>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleAdd}
            disabled={optionsQuery.isError}
          >
            Create reflex
          </Button>
        </Group>
        <Paper withBorder p="md">
          <Group align="flex-end" wrap="wrap">
            <TextInput
              label="Search reflexes"
              placeholder="Search by name or description"
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={handleSearch}
              style={{ flex: 1, minWidth: 260 }}
            />
            <Select
              label="Status"
              placeholder="All statuses"
              clearable
              value={status}
              onChange={handleStatus}
              data={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
              w={170}
            />
            <Select
              label="Sort"
              value={`${sortBy}:${sortOrder}`}
              onChange={handleSort}
              allowDeselect={false}
              data={[
                { value: "name:asc", label: "Name A–Z" },
                { value: "name:desc", label: "Name Z–A" },
                { value: "updatedAt:desc", label: "Recently updated" },
                { value: "versionNumber:desc", label: "Highest version" },
              ]}
              w={210}
            />
            <Button
              variant="subtle"
              leftSection={<IconRefresh size={16} />}
              loading={query.isFetching || optionsQuery.isFetching}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
          </Group>
        </Paper>
        {optionsQuery.isError && (
          <Alert
            icon={<IconAlertCircle size={18} />}
            title="Panel and ICD-10 options could not be loaded"
            color="red"
            role="alert"
          >
            Refresh before creating or editing a reflex.
          </Alert>
        )}
        <Paper withBorder p="md">
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={600}>Reflexes ({total})</Text>
              <Group gap="md">
                <Text size="sm" c="dimmed">
                  {visibleRange}
                </Text>
                <Select
                  label="Rows per page"
                  value={String(pageSize)}
                  onChange={handlePageSize}
                  data={["20", "50", "100"]}
                  allowDeselect={false}
                  w={130}
                  size="xs"
                />
                {totalPages > 1 && (
                  <Pagination
                    value={page}
                    total={totalPages}
                    onChange={setPage}
                    size="sm"
                  />
                )}
              </Group>
            </Group>
            {query.isError ? (
              <Alert
                icon={<IconAlertCircle size={18} />}
                title="Reflexes could not be loaded"
                color="red"
                role="alert"
              >
                Refresh and try again.
              </Alert>
            ) : query.isLoading ? (
              <Center py="xl">
                <Loader aria-label="Loading reflexes" />
              </Center>
            ) : reflexes.length === 0 ? (
              <Center py="xl">
                <Stack align="center" gap="xs">
                  <Text fw={600}>No reflexes found</Text>
                  <Text size="sm" c="dimmed">
                    Create a reflex or change the search and status filters.
                  </Text>
                </Stack>
              </Center>
            ) : (
              <Table.ScrollContainer minWidth={1000}>
                <Table verticalSpacing="sm" highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Starting panel</Table.Th>
                      <Table.Th>Follow-on rules (any ICD match)</Table.Th>
                      <Table.Th>Version</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th ta="right">Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {reflexes.map((reflex) => (
                      <ReflexAdminRow
                        key={reflex.id}
                        reflex={reflex}
                        onEdit={handleEdit}
                        onToggle={handleToggle}
                      />
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Stack>
        </Paper>
        {editorOpened && (
          <ReflexFormModal
            key={editing?.id ?? "new"}
            opened={editorOpened}
            reflex={editing}
            panelOptions={panelOptions}
            icdOptions={icdOptions}
            isSubmitting={isSaving}
            onClose={handleClose}
            onSubmit={handleSave}
          />
        )}
        <ReflexStatusConfirmModal
          reflex={toggling}
          isSubmitting={updateStatus.isPending}
          onClose={handleToggleClose}
          onConfirm={handleToggleConfirm}
        />
      </Stack>
    </Container>
  );
}
